import { useEffect, useMemo, useState } from 'react';
import {
  Stack, Title, Card, Group, Button, Table, Text, TextInput, Textarea,
  ScrollArea, Box, Affix, ActionIcon, Tooltip, Drawer, Grid, Divider, Badge, Skeleton,
} from '@mantine/core';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import {
  IconAddressBook, IconUserPlus, IconUserMinus, IconUsersPlus, IconPlane, IconSearch, IconTrash, IconDeviceFloppy,
} from '@tabler/icons-react';
import { RoleSelector } from '../components/RoleSelector.tsx';
import { TravelDetailsPanel } from '../components/TravelDetailsPanel.tsx';
import { useHeaderSlot } from '../layout/HeaderSlot.tsx';
import { useRememberedRole } from '../hooks/useRememberedRole.ts';
import {
  useVisitorGroups, useSaveVisitorGroup, useDeleteVisitorGroup,
} from '../api/hooks/useVisitorGroups.ts';
import { useVisitors, useSaveVisitor } from '../api/hooks/useVisitors.ts';
import { useQueryClient } from '@tanstack/react-query';
import type { VisitorGroup } from '../api/schemas/visitorGroup.ts';
import type { Visitor } from '../api/schemas/visitor.ts';

/**
 * Groups workspace — three-column layout mirroring the Home page:
 *   left:   searchable list of groups in the current role
 *   centre: group editor (name + two-pane membership: available / members)
 *   right:  Travel Details panel scoped to the selected group (shared itinerary)
 *
 * Per legacy behaviour, group members can't have their travel legs edited
 * individually (Home enforces that), so the only place to manage a group's
 * travel is here.
 */
const LG_BREAKPOINT = '(min-width: 75em)';

export function VisitorGroupsPage() {
  const [role, setRole] = useRememberedRole();
  const groupsQuery = useVisitorGroups(role);
  const visitors = useVisitors({ role: role ?? -1, page: 0, size: 1000 });

  const [selectedGroupId, setSelectedGroupId] = useState<number | 'new' | null>(null);
  const [addLegSignal, setAddLegSignal] = useState(0);

  const [drawerOpened, drawer] = useDisclosure(false);
  const [columnVisible, setColumnVisible] = useState(true);
  const isWide = useMediaQuery(LG_BREAKPOINT, true);

  const toggleList = () => {
    if (isWide) setColumnVisible(v => !v);
    else drawer.toggle();
  };

  const showInlineColumn = isWide && columnVisible;

  const buttonTooltip = isWide
    ? (columnVisible ? 'Hide groups' : 'Show groups')
    : (drawerOpened ? 'Close groups' : 'Open groups');

  useHeaderSlot({
    left: useMemo(
      () => (
        <Tooltip label={buttonTooltip} position="bottom" withArrow>
          <ActionIcon
            variant="white"
            color="blue"
            size="lg"
            radius="md"
            onClick={toggleList}
            aria-label="Toggle group list"
          >
            <IconAddressBook size={22} />
          </ActionIcon>
        </Tooltip>
      ),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [buttonTooltip, isWide, columnVisible, drawerOpened],
    ),
    right: useMemo(
      () => (
        <RoleSelector
          value={role}
          onChange={r => { setRole(r); setSelectedGroupId(null); }}
        />
      ),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [role],
    ),
  });

  const numericGroupId = typeof selectedGroupId === 'number' ? selectedGroupId : null;

  const listPanel = (closeAfterSelect: boolean) => (
    <GroupListPanel
      groups={groupsQuery.data ?? []}
      loading={groupsQuery.isPending || visitors.isPending}
      selectedId={numericGroupId}
      onSelect={id => {
        setSelectedGroupId(id);
        if (closeAfterSelect) drawer.close();
      }}
    />
  );

  const centreSpan = showInlineColumn
    ? { base: 12 as const, md: 8 as const, lg: 6 as const }
    : { base: 12 as const, md: 8 as const, lg: 8 as const };
  const rightSpan = showInlineColumn
    ? { base: 12 as const, md: 4 as const, lg: 3 as const }
    : { base: 12 as const, md: 4 as const, lg: 4 as const };

  return (
    <Box pos="relative" h="100%">
      <Drawer
        opened={drawerOpened}
        onClose={drawer.close}
        position="left"
        size="xs"
        title="Groups"
      >
        <Box h="calc(100vh - 88px)" style={{ display: 'flex' }}>
          {listPanel(true)}
        </Box>
      </Drawer>

      <Grid gutter="sm" align="stretch">
        {showInlineColumn && (
          <Grid.Col span={3}>
            <Card withBorder padding="xs" radius="md" h="100%" mih={500}>
              {listPanel(false)}
            </Card>
          </Grid.Col>
        )}

        <Grid.Col span={centreSpan}>
          <Card withBorder padding="sm" radius="md">
            <GroupEditorPanel
              key={selectedGroupId == null ? 'none' : String(selectedGroupId)}
              groupId={selectedGroupId}
              roleId={role}
              allVisitors={visitors.data?.rows ?? []}
              onSaved={g => { if (g.id != null) setSelectedGroupId(g.id); }}
              onDeleted={() => setSelectedGroupId(null)}
            />
          </Card>
        </Grid.Col>

        <Grid.Col span={rightSpan}>
          <Card withBorder padding="sm" radius="md" h="100%" mih={500}>
            <TravelDetailsPanel
              groupId={numericGroupId}
              addLegSignal={addLegSignal}
            />
          </Card>
        </Grid.Col>
      </Grid>

      <Affix position={{ bottom: 24, left: 24 }}>
        <Tooltip label="New group" position="right" withArrow>
          <ActionIcon
            size={56}
            radius="xl"
            color="blue"
            disabled={role == null}
            onClick={() => setSelectedGroupId('new')}
            aria-label="New group"
          >
            <IconUsersPlus size={26} />
          </ActionIcon>
        </Tooltip>
      </Affix>

      <Affix position={{ bottom: 24, right: 24 }}>
        <Tooltip label={numericGroupId == null ? 'Pick or save a group first' : 'Add travel'} position="left" withArrow>
          <ActionIcon
            size={56}
            radius="xl"
            color="blue"
            disabled={numericGroupId == null}
            onClick={() => setAddLegSignal(n => n + 1)}
            aria-label="Add travel"
          >
            <IconPlane size={26} />
          </ActionIcon>
        </Tooltip>
      </Affix>
    </Box>
  );
}

function GroupListPanel({
  groups, loading, selectedId, onSelect,
}: {
  groups: VisitorGroup[];
  loading: boolean;
  selectedId: number | null;
  onSelect(id: number): void;
}) {
  const [filter, setFilter] = useState('');

  const rows = useMemo(() => {
    const f = filter.trim().toLowerCase();
    if (!f) return groups;
    return groups.filter(g => (g.name ?? '').toLowerCase().includes(f));
  }, [groups, filter]);

  return (
    <Stack gap="xs" h="100%" style={{ minHeight: 0 }}>
      <TextInput
        leftSection={<IconSearch size={16} />}
        placeholder="Search"
        value={filter}
        onChange={e => setFilter(e.currentTarget.value)}
        size="sm"
      />
      {loading ? (
        <Stack gap={4}>{[1, 2, 3, 4].map(i => <Skeleton key={i} h={24} />)}</Stack>
      ) : rows.length === 0 ? (
        <Text c="dimmed" size="sm" ta="center" pt="md">
          {filter ? 'No matches.' : 'No groups yet.'}
        </Text>
      ) : (
        <Box style={{ flex: 1, minHeight: 0 }}>
          <ScrollArea h="100%" type="auto">
            <Table highlightOnHover withRowBorders={false} verticalSpacing="xs" fz="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Name</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map(g => {
                  const selected = g.id === selectedId;
                  return (
                    <Table.Tr
                      key={g.id}
                      onClick={() => g.id != null && onSelect(g.id)}
                      style={{
                        cursor: 'pointer',
                        background: selected ? 'var(--mantine-color-blue-light)' : undefined,
                      }}
                    >
                      <Table.Td>{g.name ?? ''}</Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        </Box>
      )}
    </Stack>
  );
}

/**
 * Centre column. Editable group name + description, two-pane membership.
 * Membership diffs are applied on Save as visitor.inGroup mutations.
 */
function GroupEditorPanel({
  groupId, roleId, allVisitors, onSaved, onDeleted,
}: {
  groupId: number | 'new' | null;
  roleId: number | null;
  allVisitors: Visitor[];
  onSaved(g: VisitorGroup): void;
  onDeleted(): void;
}) {
  const save = useSaveVisitorGroup();
  const removeGroup = useDeleteVisitorGroup();
  const saveVisitor = useSaveVisitor();
  const qc = useQueryClient();
  const isNew = groupId === 'new';
  const numericId = typeof groupId === 'number' ? groupId : null;

  const groups = useVisitorGroups(roleId);
  const currentGroup = useMemo(
    () => (numericId != null ? groups.data?.find(g => g.id === numericId) : null),
    [groups.data, numericId],
  );

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const initialMembers = useMemo(() => {
    if (numericId == null) return new Set<number>();
    return new Set<number>(
      allVisitors.filter(v => v.inGroup === numericId && v.id != null).map(v => v.id!),
    );
  }, [allVisitors, numericId]);

  const [memberIds, setMemberIds] = useState<Set<number>>(initialMembers);

  // Re-sync editor state when the selected group changes or its data refetches.
  useEffect(() => {
    if (isNew) {
      setName('');
      setDescription('');
      setMemberIds(new Set());
    } else if (currentGroup) {
      setName(currentGroup.name ?? '');
      setDescription(currentGroup.description ?? '');
    }
  }, [isNew, currentGroup?.id, currentGroup?.name, currentGroup?.description]);

  useEffect(() => {
    setMemberIds(initialMembers);
  }, [initialMembers]);

  if (groupId == null) {
    return (
      <Stack align="center" justify="center" h="100%" gap="xs" mih={400}>
        <Text c="dimmed">Select a group on the left, or add a new one.</Text>
      </Stack>
    );
  }

  const memberFullName = (v: Visitor) =>
    `${v.firstName ?? ''} ${v.lastName ?? ''}`.trim() || `#${v.id}`;

  const available = allVisitors.filter(v =>
    v.id != null
    && !memberIds.has(v.id)
    && (v.inGroup == null || v.inGroup === numericId),
  );

  const members = Array.from(memberIds)
    .map(id => allVisitors.find(v => v.id === id))
    .filter((v): v is Visitor => v != null);

  function add(id: number) {
    setMemberIds(prev => new Set([...prev, id]));
  }
  function removeOne(id: number) {
    setMemberIds(prev => {
      const n = new Set(prev);
      n.delete(id);
      return n;
    });
  }

  async function onSave() {
    if (!name.trim()) {
      notifications.show({ message: 'Group name is required', color: 'red' });
      return;
    }
    if (roleId == null) {
      notifications.show({ message: 'Pick a role first', color: 'red' });
      return;
    }
    try {
      const saved = await save.mutateAsync({
        id: numericId ?? null,
        name: name.trim(),
        description: description.trim() || null,
        entriesId: roleId,
      });
      const newId = saved.id;
      if (newId == null) throw new Error('server did not return group id');

      const adds: number[] = [];
      const removes: number[] = [];
      for (const id of memberIds) if (!initialMembers.has(id)) adds.push(id);
      for (const id of initialMembers) if (!memberIds.has(id)) removes.push(id);

      const ops: Promise<unknown>[] = [];
      for (const id of adds) {
        const v = allVisitors.find(x => x.id === id);
        if (v) ops.push(saveVisitor.mutateAsync({ ...v, inGroup: newId }));
      }
      for (const id of removes) {
        const v = allVisitors.find(x => x.id === id);
        if (v) ops.push(saveVisitor.mutateAsync({ ...v, inGroup: null }));
      }
      await Promise.all(ops);

      notifications.show({ message: isNew ? 'Group created' : 'Group saved', color: 'green' });
      onSaved(saved);
    } catch (err) {
      notifications.show({
        message: 'Save failed' + (err instanceof Error ? `: ${err.message}` : ''),
        color: 'red',
      });
    }
  }

  function onDelete() {
    if (numericId == null) return;
    modals.openConfirmModal({
      title: 'Delete group',
      children: (
        <Text size="sm">
          This removes the group. Current members are kept; their group link is cleared. Continue?
        </Text>
      ),
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          // The server releases all members (clears visitor.in_group) as part
          // of the delete, so we don't clear them one-by-one here — the old
          // loop used the possibly-stale local member set and could leave a
          // just-removed member pointing at the deleted group. Refresh the
          // visitor cache afterwards so the freed members show as ungrouped.
          await removeGroup.mutateAsync(numericId);
          await qc.invalidateQueries({ queryKey: ['visitors'] });
          notifications.show({ message: 'Group deleted', color: 'green' });
          onDeleted();
        } catch (err) {
          notifications.show({
            message: 'Delete failed' + (err instanceof Error ? `: ${err.message}` : ''),
            color: 'red',
          });
        }
      },
    });
  }

  return (
    <Stack gap="sm">
      <Group align="center" gap="xs">
        <Title order={4}>Group:</Title>
        <TextInput
          variant="unstyled"
          size="md"
          value={name}
          onChange={e => setName(e.currentTarget.value)}
          placeholder="Group name"
          style={{ flex: 1 }}
        />
      </Group>

      <Textarea
        size="xs"
        value={description}
        onChange={e => setDescription(e.currentTarget.value)}
        placeholder="Description (optional)"
        autosize
        minRows={1}
        maxRows={2}
      />

      <Divider my={4} />

      <Grid gutter="xs" align="flex-start">
        <Grid.Col span={6}>
          <Stack gap={4} style={{ minWidth: 0 }}>
            <Group justify="space-between" gap={4}>
              <Text size="sm" fw={500}>Available travellers</Text>
              <Badge variant="light" size="xs">{available.length}</Badge>
            </Group>
            <ScrollArea h={360} type="auto">
              <Table verticalSpacing={4} fz="sm">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>First Name</Table.Th>
                    <Table.Th>Last Name</Table.Th>
                    <Table.Th style={{ width: 36 }} />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {available.length === 0 && (
                    <Table.Tr>
                      <Table.Td colSpan={3}>
                        <Text c="dimmed" size="xs" ta="center" py="md">Nobody to add.</Text>
                      </Table.Td>
                    </Table.Tr>
                  )}
                  {available.map(v => (
                    <Table.Tr key={v.id}>
                      <Table.Td>{v.firstName ?? ''}</Table.Td>
                      <Table.Td>{v.lastName ?? ''}</Table.Td>
                      <Table.Td>
                        <ActionIcon variant="subtle" color="blue" onClick={() => v.id != null && add(v.id)} aria-label="Add">
                          <IconUserPlus size={16} />
                        </ActionIcon>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </ScrollArea>
          </Stack>
        </Grid.Col>
        <Grid.Col span={6}>
          <Stack gap={4} style={{ minWidth: 0 }}>
            <Group justify="space-between" gap={4}>
              <Text size="sm" fw={500}>Travellers In the Group</Text>
              <Badge variant="light" size="xs">{members.length}</Badge>
            </Group>
            <Box style={{ minHeight: 200, padding: 8, border: '1px solid var(--mantine-color-default-border)', borderRadius: 4 }}>
              {isNew && members.length === 0 ? (
                <Text c="dimmed" size="xs">Save the group first to add travellers.</Text>
              ) : members.length === 0 ? (
                <Text c="dimmed" size="xs">No members yet.</Text>
              ) : (
                <Group gap={4} wrap="wrap">
                  {members.map(v => (
                    <Badge
                      key={v.id}
                      variant="light"
                      size="lg"
                      rightSection={
                        <ActionIcon
                          size="xs"
                          variant="transparent"
                          color="gray"
                          onClick={() => v.id != null && removeOne(v.id)}
                          aria-label="Remove"
                        >
                          <IconUserMinus size={12} />
                        </ActionIcon>
                      }
                    >
                      {memberFullName(v)}
                    </Badge>
                  ))}
                </Group>
              )}
            </Box>
          </Stack>
        </Grid.Col>
      </Grid>

      <Group gap="xs" mt="xs">
        <Button
          size="xs"
          color="blue"
          leftSection={<IconDeviceFloppy size={14} />}
          onClick={onSave}
          loading={save.isPending || saveVisitor.isPending}
        >
          Save
        </Button>
        <Button
          size="xs"
          color="red"
          variant="light"
          leftSection={<IconTrash size={14} />}
          onClick={onDelete}
          disabled={isNew || numericId == null}
        >
          Delete
        </Button>
      </Group>
    </Stack>
  );
}
